/**
 * The AI assistant panel.
 *
 * It streams, and it shows the trace id for every answer. That is deliberate:
 * an AI-native product should let a curious user - and any engineer looking
 * over their shoulder - see exactly which run produced an answer.
 */
import { useEffect, useRef, useState } from "react";
import { api, ApiError, streamAssistance } from "@/api/client";
import type { AssistantReply } from "@/api/types";

interface Turn {
  role: "user" | "assistant";
  text: string;
  traceId?: string;
  route?: string;
  pending?: boolean;
}

const SUGGESTIONS = [
  "Something like Night Kitchen but darker",
  "A dark science-fiction film with a female lead",
  "What am I part way through?",
];

export function Assistant() {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string>();
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [turns]);

  async function ask(message: string) {
    if (!message.trim() || busy) return;
    setDraft("");
    setBusy(true);
    setTurns((current) => [
      ...current,
      { role: "user", text: message },
      { role: "assistant", text: "", pending: true },
    ]);

    const replace = (patch: Partial<Turn>) =>
      setTurns((current) => {
        const next = [...current];
        next[next.length - 1] = { ...next[next.length - 1], ...patch };
        return next;
      });

    try {
      let streamed = "";
      for await (const event of streamAssistance(message, conversationId)) {
        if (event.type === "start" && typeof event.conversation_id === "string") {
          setConversationId(event.conversation_id);
        } else if (event.type === "status" && typeof event.message === "string") {
          replace({ text: event.message, pending: true });
        } else if (event.type === "text" && typeof event.text === "string") {
          streamed += event.text;
          replace({ text: streamed, pending: true });
        } else if (event.type === "done") {
          replace({
            text: typeof event.text === "string" && event.text ? event.text : streamed,
            traceId: typeof event.trace_id === "string" ? event.trace_id : undefined,
            route: typeof event.route === "string" ? event.route : undefined,
            pending: false,
          });
          if (typeof event.conversation_id === "string") setConversationId(event.conversation_id);
        }
      }
    } catch {
      // Streaming can be blocked by a proxy; the non-streaming path always works.
      try {
        const reply = await api.post<AssistantReply>("/api/v1/ai/assistance", {
          message,
          conversation_id: conversationId ?? "",
          surface: "viewer",
        });
        setConversationId(reply.conversation_id);
        replace({ text: reply.text, traceId: reply.trace_id, route: reply.route, pending: false });
      } catch (fallbackError) {
        const detail =
          fallbackError instanceof ApiError ? fallbackError.message : "The assistant is offline.";
        replace({ text: detail, pending: false });
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="assistant-fab" onClick={() => setOpen(true)}>
        Ask Hayfish
      </button>
    );
  }

  return (
    <aside className="assistant" aria-label="Hayfish assistant">
      <header className="assistant__head">
        <strong>Ask Hayfish</strong>
        <span className="chip chip--accent">AI</span>
        <div className="nav__spacer" />
        <button type="button" className="btn btn--quiet" onClick={() => setOpen(false)}>
          Close
        </button>
      </header>

      <div className="assistant__log" ref={logRef}>
        {turns.length === 0 && (
          <div className="stack">
            <p style={{ color: "var(--paper-300)", margin: 0 }}>
              Ask for something to watch. Answers come from the real catalog.
            </p>
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="btn btn--ghost"
                onClick={() => void ask(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
        {turns.map((turn, index) => (
          <div key={index} className={`bubble bubble--${turn.role}`}>
            {turn.text || (turn.pending ? "Thinking…" : "")}
            {turn.traceId && (
              <span className="bubble__trace">
                {turn.route === "agent" ? "agent" : "model"} · trace {turn.traceId.slice(0, 12)}
              </span>
            )}
          </div>
        ))}
      </div>

      <form
        className="assistant__form"
        onSubmit={(event) => {
          event.preventDefault();
          void ask(draft);
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="What should I watch?"
          aria-label="Message the assistant"
          disabled={busy}
        />
        <button type="submit" className="btn btn--accent" disabled={busy || !draft.trim()}>
          Send
        </button>
      </form>
    </aside>
  );
}
